// ordersExport.js
//
// Excel export for the Distributor Orders grid. Mirrors the on-screen layout
// (one column per order; rows = order fields, then SKU quantities, then
// totals). Lazy-uses ExcelJS and a dependency-free anchor download.

import { monthLabel } from "../utils/monthDate.js";

const FIELD_ROWS = [
  { key: "state",             label: "State" },
  { key: "distributor",       label: "Distributor" },
  { key: "warehouse",         label: "Warehouse" },
  { key: "status",            label: "Status" },
  { key: "target_ship_month", label: "Target Ship Month" },
  { key: "po_receipt_date",   label: "PO Receipt Date" },
  { key: "pickup_date",       label: "Pickup Date" },
  { key: "copacker_po",       label: "CoPacker PO #" },
  { key: "distributor_po",    label: "Dist PO #" },
  { key: "notes",             label: "Notes" },
];

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function exportOrdersToExcel({ orders, items, displaySkus, sizes = [] }) {
  // Loaded on demand — ExcelJS is ~400kB and only an export needs it.
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = "Brand Command";
  wb.created = new Date();
  const ws = wb.addWorksheet("Distributor Orders");

  // Quantity lookup: order_id → sku_name → qty
  const qtyByOrder = {};
  for (const it of items || []) {
    (qtyByOrder[it.order_id] ||= {})[it.sku_name] = it.quantity ?? 0;
  }

  const NAVY  = { argb: "FF1E3A5F" };
  const WHITE = { argb: "FFFFFFFF" };
  const SECTION_FILL = { argb: "FFE2E8F0" };
  const thin = { style: "thin", color: { argb: "FFD1D5DB" } };

  const totalCols = 1 + orders.length;

  // Header row: blank corner + one column per order ("CoPacker PO #" / meta).
  const headerVals = [""];
  orders.forEach(o => headerVals.push(o.copacker_po ? `CoPacker PO #${o.copacker_po}` : "New Order"));
  const hr = ws.addRow(headerVals);
  hr.height = 20;
  hr.eachCell(cell => {
    cell.font = { bold: true, color: WHITE, size: 11 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: NAVY };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = { bottom: thin };
  });

  const sectionRow = (label) => {
    const r = ws.addRow([label, ...orders.map(() => "")]);
    r.eachCell(cell => {
      cell.font = { bold: true, size: 10 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: SECTION_FILL };
      cell.border = { bottom: thin };
    });
  };

  sectionRow("Order Info");
  for (const fr of FIELD_ROWS) {
    // target_ship_month is stored as a month-start date (migration 0085) —
    // write it the way the grid reads it, "January 2026", not "2026-01-01".
    const cellOf = (o) => (fr.key === "target_ship_month"
      ? (monthLabel(o[fr.key]) || "")
      : (o[fr.key] ?? ""));
    const r = ws.addRow([fr.label, ...orders.map(cellOf)]);
    r.getCell(1).font = { bold: true, size: 10 };
    r.eachCell((cell, c) => { cell.border = { bottom: thin }; if (c > 1) cell.alignment = { horizontal: "left" }; });
  }

  sectionRow("ProductSKU");
  for (const sku of displaySkus || []) {
    const r = ws.addRow([sku, ...orders.map(o => qtyByOrder[o.id]?.[sku] || 0)]);
    r.getCell(1).font = { size: 10 };
    r.eachCell((cell, c) => { cell.border = { bottom: thin }; if (c > 1) { cell.numFmt = "#,##0"; cell.alignment = { horizontal: "right" }; } });
  }

  sectionRow("Totals");
  const sizeTotal = (o, size) => (displaySkus || []).reduce((s, sku) => {
    const skuSize = sku.split(" / ")[1] ?? "";
    return s + (skuSize === size ? (qtyByOrder[o.id]?.[sku] || 0) : 0);
  }, 0);
  const totalCases = (o) => (displaySkus || []).reduce((s, sku) => s + (qtyByOrder[o.id]?.[sku] || 0), 0);

  [...sizes.map((size) => [`Total ${size}`, (o) => sizeTotal(o, size)]),
   ["Total Cases", (o) => totalCases(o)]].forEach(([label, fn]) => {
    const r = ws.addRow([label, ...orders.map(fn)]);
    r.eachCell((cell, c) => {
      cell.font = { bold: true, size: 10 };
      cell.border = { top: thin, bottom: thin };
      if (c > 1) { cell.numFmt = "#,##0"; cell.alignment = { horizontal: "right" }; }
    });
  });

  ws.getColumn(1).width = 22;
  for (let i = 2; i <= totalCols; i++) ws.getColumn(i).width = 16;

  const buf = await wb.xlsx.writeBuffer();
  saveBlob(
    new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `Distributor_Orders_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
}
